import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MessageStatusIcon } from './MessageStatusIcon';
import { ReactionChips } from './ReactionChips';
import { MediaImage } from './media/MediaImage';
import { MediaVideo } from './media/MediaVideo';
import { MediaGif } from './media/MediaGif';
import { MediaAudio } from './media/MediaAudio';
import { MediaDocument } from './media/MediaDocument';
import { MediaSticker } from './media/MediaSticker';
import {
  Bot,
  User,
  Clock,
  Copy,
  MoreHorizontal,
  MapPin,
  Image,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { FormattedMessageContent } from './FormattedMessageContent';
import { FlowResponsePreview } from './FlowResponsePreview';
import { FlowScreenPreview } from './FlowScreenPreview';
import { useFlowDefinition, extractFlowIdFromMessage } from '@/hooks/useFlowDefinition';
import { format } from 'date-fns';
import { useAuth } from '@/modules/auth';
import { supabase } from '@/integrations/supabase/client';
import { AIMediaInsights } from './AIMediaInsights';
import { QuotedMessage } from './QuotedMessage';
import { ImageZoomModal } from '@/components/ui/image-zoom-modal';
import type { Message } from '@/types/message-types';
import { getInteractivePayload } from '@/lib/message-utils';
import { useEmbeddedMessageStatus } from '@/hooks/useEmbeddedMessageStatus';
import { useUserLookup } from '@/hooks/useUserLookup';
import { AggregatedReaction } from '@/utils/reactionUtils';

interface MessageItemProps {
  message: Message;
  customerName?: string;
  tenantId?: string;
  onCopyMessage: (content: string) => void;
  onCopyImage?: (imageUrl: string, messageId: string) => void;
  reactions?: AggregatedReaction[];
  onScrollToMessage?: (whatsappMessageId: string) => void;
  isHighlighted?: boolean;
}

export const MessageItem = React.memo(function MessageItem({
  message,
  customerName = 'Customer',
  tenantId: propTenantId,
  onCopyMessage,
  onCopyImage,
  reactions = [],
  onScrollToMessage,
  isHighlighted = false
}: MessageItemProps) {
  const { t } = useTranslation('conversations');
  const { tenantId: authTenantId } = useAuth();
  const tenantId = propTenantId ?? authTenantId;
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [showInteractiveImageZoom, setShowInteractiveImageZoom] = useState(false);
  const [interactiveImageUrl, setInteractiveImageUrl] = useState('');
  const { getMessageStatus } = useEmbeddedMessageStatus();
  const { getUserName } = useUserLookup();

  // Fetch flow definition for nfm_reply messages (V1205)
  const flowId = message.message_type === 'nfm_reply' ? extractFlowIdFromMessage(message) : null;
  const { data: flowDefinition, isLoading: flowLoading, error: flowError } = useFlowDefinition(flowId, {
    enabled: message.message_type === 'nfm_reply' && !!flowId
  });

  const getSenderIcon = () => {
    switch (message.sender_type) {
      case 'ai_agent_automatic':
      case 'ai_with_human_review_no_changes':
      case 'ai_with_human_review_with_changes':
        return <Bot className="h-4 w-4" />;
      case 'customer':
        return <User className="h-4 w-4" />;
      case 'system':
        return <Bot className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getSenderName = () => {
    switch (message.sender_type) {
      case 'ai_agent_automatic':
        return message.ai_model ? `AI (${message.ai_model})` : 'AI Assistant';
      case 'ai_with_human_review_no_changes':
        return 'AI (Human Reviewed)';
      case 'ai_with_human_review_with_changes':
        return 'AI + Human (Modified)';
      case 'customer':
        return customerName || 'Customer';
      case 'human_agent':
        // Try to get the actual user name from sender_uuid (UUID lookup)
        if (message.sender_uuid) {
          const userName = getUserName(message.sender_uuid);
          if (userName) {
            return userName;
          }
        }

        // Fallback to readable sender_id if formatted (not a UUID)
        if (message.sender_id && message.sender_id !== message.sender_uuid) {
          // Parse out phone number if present (for legacy messages)
          // Format: "Name | +56947707463" → extract only "Name"
          return message.sender_id.split('|')[0].trim();
        }

        // Final fallback
        return 'You';
      case 'system':
        return 'System';
      default:
        return 'Customer';
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'HH:mm');
    } catch {
      return '--:--';
    }
  };

  const isOutgoing = ['human_agent', 'ai_agent_automatic', 'ai_with_human_review_no_changes', 'ai_with_human_review_with_changes', 'system'].includes(message.sender_type);
  

  // Detect if message is a GIF - WhatsApp sends GIFs as stickers with webp format
  const isGif = (message.message_type === 'video' && 
    (message.metadata?.mime_type?.includes('gif') || 
     message.metadata?.media_type?.includes('gif'))) ||
    (message.message_type === 'sticker' && 
     message.metadata?.mime_type?.includes('webp'));

  // Handle image URL updates for copy functionality
  useEffect(() => {
    if (message.message_type === 'image' && tenantId) {
      const loadImageUrl = async () => {
        const { whatsappMediaService } = await import('@/services/whatsappMediaService');
        const mediaId = whatsappMediaService.extractMediaIdFromMessage(message);
        if (mediaId) {
          const result = await whatsappMediaService.downloadAndGetMediaUrl(
            tenantId, 
            mediaId, 
            'image',
            message.id
          );
          if (result.success && result.fileUrl) {
            setImageUrl(result.fileUrl);
          }
        }
      };
      loadImageUrl();
    }
  }, [message, tenantId]);

  const renderMessageContent = () => {
    if (!tenantId) {
      return <span>{message.content}</span>;
    }

    switch (message.message_type) {
      case 'image':
        return <MediaImage message={message} tenantId={tenantId} />;
      case 'video':
        return isGif ? 
          <MediaGif message={message} tenantId={tenantId} /> :
          <MediaVideo message={message} tenantId={tenantId} />;
      case 'audio':
        return <MediaAudio message={message} tenantId={tenantId} />;
      case 'document':
        return <MediaDocument message={message} tenantId={tenantId} />;
      case 'sticker':
        return isGif ? 
          <MediaGif message={message} tenantId={tenantId} /> :
          <MediaSticker message={message} tenantId={tenantId} />;
      case 'location':
        return (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>{message.content || '📍 Location'}</span>
          </div>
        );
      case 'contact':
        // V1300 Phase 2: Display contact (vCard)
        return (
          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
            <User className="h-4 w-4" />
            <span className="text-sm">{message.content || '📇 Contact'}</span>
          </div>
        );
      case 'list_reply':
        // V1300 Phase 2: Display list menu selection
        return (
          <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <span className="text-sm">
              {message.metadata?.list_reply_title || message.content || 'Selected option'}
            </span>
          </div>
        );
      case 'unknown':
        // V1300 Phase 2: Display unsupported message type
        return (
          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg text-muted-foreground">
            <span className="text-sm">{t('message.unsupportedType')}</span>
          </div>
        );
      case 'reaction':
        return (
          <div className="flex items-center gap-1 p-2 bg-muted/50 rounded-lg text-sm">
            <span className="text-lg">{message.metadata?.reaction_emoji || '👍'}</span>
            <span className="text-muted-foreground">{t('message.reacted')}</span>
          </div>
        );
      case 'nfm_reply':
        // V1205: Render WhatsApp Flow completion with FlowPreview
        return (
          <FlowResponsePreview
            message={message}
            flowJson={flowDefinition?.flow_json}
            flowName={flowDefinition?.template_name}
            isLoading={flowLoading}
            error={flowError ? 'Could not load flow definition' : null}
          />
        );
      case 'wa_flow_screen':
        // V1206: Render WhatsApp Flow screen progress
        return <FlowScreenPreview message={message} />;
      case 'template': {
        // Render template messages with optional header, footer, and CTA buttons
        const tmplButtons = (message.metadata?.template_buttons || []) as Array<{
          type: string; text: string; url?: string;
        }>;
        const tmplHeader = message.metadata?.template_header as string | undefined;
        const tmplFooter = message.metadata?.template_footer as string | undefined;
        // Set by persist-sent-template.ts's `persistFailedTemplate` — the classic inbox's own
        // copy of the state signal added to TemplateContent.tsx (new inbox), same source row.
        const tmplFailed = message.metadata?.send_failed === true;
        const tmplFailureReason = message.metadata?.failure_reason as string | undefined;

        // Strip trailing 🔗 lines from content (backward compat with earlier persist)
        const bodyContent = message.content.replace(/\n\n🔗\s.+$/s, '').trim();

        return (
          <div className="space-y-2">
            {tmplHeader && (
              <div className="font-semibold text-sm">{tmplHeader}</div>
            )}
            <FormattedMessageContent content={bodyContent} />
            {tmplFooter && (
              <div className="text-xs text-muted-foreground">{tmplFooter}</div>
            )}
            {tmplFailed && (
              <div className="flex items-start gap-1.5 text-[11px] text-red-600 dark:text-red-300">
                <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" aria-hidden="true" />
                <span>{tmplFailureReason ? t('templateContent.notSentWithReason', { reason: tmplFailureReason }) : t('templateContent.notSent')}</span>
              </div>
            )}
            {tmplButtons.length > 0 && (
              <div className="space-y-2 mt-2">
                {tmplButtons.map((btn, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg"
                  >
                    {btn.type === 'URL' || btn.type === 'url' ? (
                      <ExternalLink className="w-4 h-4 mr-2 text-gray-500" />
                    ) : (
                      <svg className="w-3.5 h-3.5 mr-2" viewBox="0 0 16 16" fill="currentColor"><path d="M9.5 3v2.5C13 6 15 9.5 15 13c-1.5-3-4-4.5-5.5-4.5V11l-4.5-4L9.5 3z" /></svg>
                    )}
                    {btn.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
      case 'interactive': {
        // Render WhatsApp interactive messages (CTA buttons, etc.) - display only.
        // Helper prefers metadata.interactive (post-PR-B1b), falls back to JSON.parse(content) for legacy rows.
        const interactive = getInteractivePayload(message) as Record<string, unknown> & {
          type?: string;
          header?: { type?: string; text?: string; image?: { link?: string } } | string;
          body?: { text?: string };
          footer?: { text?: string };
          action?: { parameters?: { display_text?: string }; buttons?: Array<{ type?: string; title?: string; id?: string; reply?: { id?: string; title?: string }; [key: string]: unknown }> };
        } | null;

        if (!interactive) {
          // Unparseable / no envelope → render as plain text
          return <FormattedMessageContent content={message.content} />;
        }

        if (interactive?.type === 'cta_url') {
            const header = interactive.header?.text;
            const body = interactive.body?.text;
            const footer = interactive.footer?.text;
            const buttonText = interactive.action?.parameters?.display_text;

            return (
              <div className="space-y-2">
                {header && (
                  <div className="font-semibold text-sm">{header}</div>
                )}
                {body && (
                  <FormattedMessageContent content={body} />
                )}
                {footer && (
                  <div className="text-xs text-muted-foreground">{footer}</div>
                )}
                {buttonText && (
                  <div className="flex items-center justify-center w-full px-4 py-3 mt-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <ExternalLink className="w-4 h-4 mr-2 text-gray-500" />
                    {buttonText}
                  </div>
                )}
              </div>
            );
          }

          // Button-type interactive messages (product cards with reply buttons)
          if (interactive?.type === 'button') {
            const header = interactive.header;
            const body = interactive.body?.text;
            const footer = interactive.footer?.text;
            const buttons = interactive.action?.buttons || [];

            return (
              <>
              <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 max-w-[220px] shadow-sm">
                {/* Image header — fills top of card like WhatsApp */}
                {header?.type === 'image' && header.image?.link && (
                  <div className="bg-gray-100 dark:bg-gray-700">
                    <img
                      src={header.image.link}
                      alt={t('message.productAlt')}
                      className="w-full h-auto max-h-[140px] object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInteractiveImageUrl(header.image.link);
                        setShowInteractiveImageZoom(true);
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {/* Text header */}
                {header?.type === 'text' && header.text && (
                  <div className="px-3 pt-2 font-semibold text-sm">{header.text}</div>
                )}
                {/* Body */}
                <div className="px-3 py-2">
                  {body && <FormattedMessageContent content={body} />}
                </div>
                {/* Footer */}
                {footer && (
                  <div className="px-3 pb-2 text-xs text-muted-foreground">{footer}</div>
                )}
                {/* Reply buttons — WhatsApp teal, separated by thin borders */}
                {buttons.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    {buttons.map((btn: { type?: string; title?: string; id?: string; [key: string]: unknown }, i: number) => (
                      <div
                        key={btn.reply?.id || i}
                        className={`w-full py-2.5 px-4 relative flex items-center justify-center text-sm font-medium text-[#00a884] dark:text-[#25D366] ${i > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}
                      >
                        <svg className="w-3.5 h-3.5 shrink-0 absolute left-4" viewBox="0 0 16 16" fill="currentColor"><path d="M9.5 3v2.5C13 6 15 9.5 15 13c-1.5-3-4-4.5-5.5-4.5V11l-4.5-4L9.5 3z" /></svg>
                        {btn.reply?.title || 'Button'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {showInteractiveImageZoom && (
                <ImageZoomModal
                  isOpen={showInteractiveImageZoom}
                  onClose={() => setShowInteractiveImageZoom(false)}
                  imageUrl={interactiveImageUrl}
                  alt={t('message.productAlt')}
                />
              )}
              </>
            );
          }

          // Fallback for other interactive types (list, etc.)
          return <FormattedMessageContent content={interactive?.body?.text || message.content} />;
      }
      default:
        // Hide legacy empty messages
        if (message.content === 'EMPTY' || message.content?.trim() === '') {
          return null;
        }
        return <FormattedMessageContent content={message.content} />;
    }
  };

  // Hide legacy empty text messages only - allow media types with empty content
  if (message.content === 'EMPTY' || (message.content?.trim() === '' && message.message_type === 'text')) {
    return null;
  }

  // DEBUG: Log reply messages metadata
  if (message.content === 'Reply' || message.metadata?.context) {
    console.log('🔍 DEBUG: MessageItem rendering:', {
      messageId: message.id.substring(0, 8),
      content: message.content,
      hasMetadata: !!message.metadata,
      metadata: message.metadata,
      hasContext: !!message.metadata?.context,
      contextId: message.metadata?.context?.id,
      tenantId
    });
  }

  return (
    <div
      data-message-id={message.id}
      data-whatsapp-message-id={message.external_id}
      className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'} mb-2 px-3 group animate-message-enter`}
    >

      <div className={`max-w-[70%] min-w-[120px] ${isOutgoing ? 'ml-16' : 'mr-16'}`}>
        {/* Sender info - show for customer messages and human_agent messages (multi-agent support) */}
        {(message.sender_type === 'customer' || message.sender_type === 'human_agent' || message.sender_type === 'system') && (
          <div className="flex items-center gap-1 mb-1 px-3">
            {getSenderIcon()}
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{getSenderName()}</span>
            {message.ai_model && (message.sender_type === 'ai_agent_automatic' || message.sender_type === 'ai_with_human_review_no_changes' || message.sender_type === 'ai_with_human_review_with_changes') && (
              <Badge variant="secondary" className="text-xs ml-1">
                {message.ai_model}
              </Badge>
            )}
            {/* Workflow Node Indicator - shows which phase sent the message */}
            {(message.sender_type === 'ai_agent_automatic' || message.sender_type === 'ai_with_human_review_no_changes' || message.sender_type === 'ai_with_human_review_with_changes') && message.metadata && (message.metadata as Record<string, unknown>).workflow_node_id && (
              <Badge variant="info" className="text-xs ml-1">
                {(() => {
                  const nodeId = (message.metadata as Record<string, unknown>).workflow_node_id;
                  const nodeName = (message.metadata as Record<string, unknown>).node_name;
                  const phaseNumber = (message.metadata as Record<string, unknown>).phase_number;

                  // Show phase number if available (e.g., "Phase 1" for node-multi-5a)
                  if (phaseNumber) {
                    return `Phase ${phaseNumber}`;
                  }

                  // Extract short name from node ID (e.g., "5a" from "node-multi-5a")
                  if (nodeId && nodeId.includes('node-multi-5')) {
                    const shortId = nodeId.replace('node-multi-', '');
                    return `Step ${shortId.toUpperCase()}`;
                  }

                  // Fallback to node name
                  if (nodeName) {
                    return nodeName;
                  }

                  return 'AI';
                })()}
              </Badge>
            )}
          </div>
        )}
        
        {/* Modern Message bubble */}
        <div
          className={`
            relative px-4 py-3 w-full break-words message-bubble overflow-hidden
            ${isOutgoing
              ? 'message-bubble-outgoing'
              : 'message-bubble-incoming'
            }
            ${message.message_type === 'image' ? 'p-1 message-bubble-image' : ''}
            ${message.message_type === 'nfm_reply' || message.message_type === 'wa_flow_screen' || message.message_type === 'interactive' ? 'p-0 bg-transparent shadow-none' : ''}
            ${isHighlighted ? 'message-highlight' : ''}
          `}
        >

          {/* Reply Context - V1300 Phase 2 */}
          {message.metadata?.context && (
            <QuotedMessage
              repliedToWhatsappId={message.metadata.context.id}
              conversationId={message.conversation_id}
              tenantId={tenantId}
              onScrollToMessage={onScrollToMessage}
            />
          )}

          {/* Message Content */}
          <div className="text-[14px]">
            {renderMessageContent()}
          </div>

          {/* Modern Message footer with time and status - hide for nfm_reply and wa_flow_screen (have own styling) */}
          {message.message_type !== 'nfm_reply' && message.message_type !== 'wa_flow_screen' && (
            <div className={`
              flex items-center justify-end mt-2 gap-1.5 min-h-[16px]
              ${message.message_type === 'image' && ['customer', 'human_agent'].includes(message.sender_type) ? 'text-white text-xs absolute bottom-2 right-2' : message.message_type === 'image' ? 'bg-black/40 text-white rounded px-2 py-1 text-xs absolute bottom-2 right-2' : ''}
            `}>
              <span className={`message-time ${message.message_type === 'image' ? 'text-white' : ''}`}>
                {formatTime(message.created_at)}
                {message.metadata?.edited && (
                  <span className="ml-1 text-[10px] opacity-70">(edited)</span>
                )}
              </span>
              {isOutgoing && (
                <MessageStatusIcon
                  message={message}
                  className={`w-[14px] h-[14px] ${message.message_type === 'image' ? 'text-white' : 'text-gray-500'}`}
                />
              )}
            </div>
          )}

          {/* Message Actions (hidden dropdown) - Hide for GIFs */}
          {!isGif && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute -right-1 -top-1 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-all duration-200 bg-background/90 backdrop-blur-sm hover:scale-105 rounded-full shadow-sm border border-border pointer-events-none group-hover:pointer-events-auto"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="shadow-lg rounded-xl p-1.5 animate-fade-in min-w-[160px]">
                {/* Copy image option for image messages */}
                {message.message_type === 'image' && imageUrl && onCopyImage && (
                  <DropdownMenuItem
                    onClick={() => onCopyImage(imageUrl, message.id)}
                    className="hover:bg-accent hover:text-accent-foreground transition-colors rounded-lg px-3 py-2 text-[13px] flex items-center gap-2"
                  >
                    <Image className="h-4 w-4" />
                    Copy image
                  </DropdownMenuItem>
                )}
                
                {/* Copy message option (hidden for stickers) */}
                {message.message_type !== 'sticker' && (
                  <DropdownMenuItem
                    onClick={() => onCopyMessage(message.content)}
                    className="hover:bg-accent hover:text-accent-foreground transition-colors rounded-lg px-3 py-2 text-[13px] flex items-center gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy message
                  </DropdownMenuItem>
                )}
                
                {/* The message-feedback control used to claim it recorded a signal it never did.
                    This menu offered "Good response" / "Poor response", called a handler that
                    only console.log'd the click, and showed a toast claiming "Feedback recorded"
                    -- nothing was ever written anywhere. Retired rather than completed: building
                    the real thing needs a per-message link to the document that produced the AI
                    reply plus a place to store the signal, which nobody has asked for yet, and an
                    inert control that claims to have worked is worse than no control. */}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* AI Media Insights */}
          {message.metadata && (message.message_type === 'image' || message.message_type === 'video') && (
            <AIMediaInsights 
              mediaType={message.message_type as string}
              mediaUrl={message.media_url || ''}
              aiMetadata={message.metadata as Record<string, unknown>} 
            />
          )}
        </div>

        {/* Reaction Chips */}
        <ReactionChips reactions={reactions} isOutgoing={isOutgoing} />

      </div>
    </div>
  );
});